/**
 * App origin labels: the single DNS label that gives each install of an App
 * its own origin, `<tenant>--<owner>--<name>`. The tenant is the workspace
 * the App runs in (a Hearth tenant, a Bear project), `<owner>/<name>` its
 * registry id.
 *
 * Every (workspace, publisher, app) triple is its own origin because
 * origin-scoped storage (Cache Storage, IndexedDB, localStorage, cookies) is
 * shared by everything on an origin, the module compile cache included
 * (`loader/module-cache.ts`). With the workspace in the label, one workspace's
 * data never sits where another workspace's copy of the same App can read it.
 * The host is a storage boundary, never an authorization one.
 *
 * Hyphen-delimited (`--`): underscores are invalid in TLS/SNI hostnames. Each
 * component is a clean DNS-label slug (lowercase `[a-z0-9]`, single interior
 * hyphens), which keeps `--` an unambiguous delimiter and the result one valid
 * label under a wildcard certificate. Components that aren't slug-safe, or a
 * label past the DNS limit, can't be served as an origin: the builders return
 * null and the launch path must surface that.
 */
const DELIM = "--";

/** A single DNS-label component: lowercase, no leading/trailing/double hyphen. */
const COMPONENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Max length of one DNS label (the whole `<tenant>--<owner>--<name>` must fit). */
const MAX_LABEL_LENGTH = 63;

/** Lowercase + validate one label component, or null if not slug-safe. */
function normalizeComponent(raw: string): string | null {
  const v = raw.toLowerCase();
  return COMPONENT.test(v) ? v : null;
}

/**
 * The single DNS label encoding a tenant's install of an app. Null if any
 * component isn't slug-safe or the combined label exceeds the DNS-label limit.
 * Every App gets this form — built-in and workspace alike — so no two workspaces
 * ever share origin-scoped storage (see the file header).
 */
export function appLabel(
  tenant: string,
  owner: string,
  name: string,
): string | null {
  const t = normalizeComponent(tenant);
  const o = normalizeComponent(owner);
  const n = normalizeComponent(name);
  if (!t || !o || !n) return null;
  const label = `${t}${DELIM}${o}${DELIM}${n}`;
  return label.length <= MAX_LABEL_LENGTH ? label : null;
}

/**
 * {@link appLabel} for a registry id: `@publisher/name` →
 * `<tenant>--publisher--name`. Null when the id isn't `@pub/name`-shaped or a
 * half isn't slug-safe. Within one tenant, origins key on the **registry
 * identity** (not the install path): two copies of the same App in a workspace
 * share one origin — they are the same app, and the projection winner decides
 * which install serves it.
 */
export function appLabelForId(tenant: string, id: string): string | null {
  if (!id.startsWith("@")) return null;
  const slash = id.indexOf("/");
  if (slash < 0) return null;
  return appLabel(tenant, id.slice(1, slash), id.slice(slash + 1));
}

/** The registry id for a parsed app host: `{owner, name}` → `@owner/name`. */
export function appIdForHost(parsed: { owner: string; name: string }): string {
  return `@${parsed.owner}/${parsed.name}`;
}

/** What an app host decomposes to. `label` is the canonical label, for comparing
 *  a host against an expected {@link appLabel} without rebuilding it by hand. */
export interface ParsedAppHost {
  tenant: string;
  owner: string;
  name: string;
  label: string;
}

/**
 * Parse an app host header back to its components, or null if `host` is not a
 * well-formed `<tenant>--<owner>--<name>.<appsDomain>` app host. A slug-safe
 * component never contains `--`, so the label splits into exactly three parts;
 * each is re-validated, so a component may contain interior hyphens but
 * `_`/`.`/`--` are rejected — symmetric with {@link appLabel}. A two-part label
 * (the retired tenant-independent form) is not an app host.
 *
 * Rejects any host with extra labels before the apps domain (e.g.
 * `evil.<tenant>--<owner>--<name>.<appsDomain>`) so a nested host can't
 * masquerade as an app origin.
 */
export function parseAppHost(
  host: string | null | undefined,
  appsDomain: string,
): ParsedAppHost | null {
  if (!host) return null;
  const hostname = host.split(":")[0]?.toLowerCase() ?? "";
  const suffix = `.${appsDomain.toLowerCase()}`;
  if (!hostname.endsWith(suffix)) return null;
  const label = hostname.slice(0, -suffix.length);
  // Exactly one label — no further dots — so a nested host can't masquerade.
  if (!label || label.includes(".") || label.length > MAX_LABEL_LENGTH) {
    return null;
  }
  const parts = label.split(DELIM);
  if (parts.length !== 3 || !parts.every((p) => COMPONENT.test(p))) return null;
  const [tenant, owner, name] = parts as [string, string, string];
  return { tenant, owner, name, label };
}
